import React, { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import WorkflowOrderFilters from 'components/orders/shared/workflow-order-filters.component';
import { getStepOrdersTemplate } from 'apollo/templates/order';
import { NetworkStatus, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { getStepOrderListRowTemplate } from 'apollo/templates/order/query.graphql';
import { useLiveOrderList } from 'hooks/useLiveOrderList';
import { useRefreshOrder } from 'hooks/useRefreshOrder';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Order, OrderFilter, OrderTableType } from 'models/order';
import { changeWorkflowStepForManyTemplate, pausedResumedStepTemplate } from 'apollo/templates/workFlow';
import { ApolloCache, Reference } from '@apollo/client/core';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import OrderTableContainer from 'components/orders/order-table-container.container';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import PageTitle from 'components/shared/page-title/page-title.component';
import { OrderBy, OrderDirection } from 'models/search-result';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { Department } from 'models/organization';
import { Backdrop, Box, Button, Checkbox, CircularProgress, FormControlLabel } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import BatchActionBar, { BATCH_SELECTION_LIMIT } from 'components/orders/shared/batch-action-bar.component';
import BatchMoveNextStepConfirmDialog from 'components/orders/shared/batch-move-next-step-confirm-dialog.component';
import {
    downloadBatchOrdersPdf,
    getBatchPdfActions,
    isBatchPdfActionDisabled,
    isBatchPdfActionType,
} from 'components/orders/shared/batch-pdf-actions';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import {
    BATCH_SEND_TO_CLINIC_ACTION,
    getBatchOrderActions,
    isBatchOrderActionDisabled,
} from 'components/orders/shared/batch-order-actions';
import { useBatchOrderActions } from 'components/orders/shared/use-batch-order-actions';
import RecentDeliveryNoteBatches from 'components/orders/shared/recent-batches/recent-delivery-note-batches.component';
import { splitOrdersByNextStep } from 'utils/next-workflow-step';
import { Steps } from 'models/workflow-step';
import { Organization } from 'models/organization';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { millingDeadlineDate } from 'utils/milling-deadline-date';
import { useTranslation } from 'react-i18next';
import { useActiveOrderListProfile } from 'components/orders/lab-order-table/use-active-order-list-profile';
import {
    getActiveOrderListColumnKeys,
    SYSTEM_DEFAULT_WORKFLOW_ORDER_LIST_PROFILE,
} from 'components/orders/lab-order-table/column-registry';
import { buildIncludeVariables } from 'components/orders/lab-order-table/build-include-variables';
import ListViewProfileSelector from 'components/orders/lab-order-table/list-view-profile-selector.component';
import { resolveTechnicianFilter } from 'components/shared/order-filters/technician-filter.utils';

interface CheckedOrder extends Order {
    checked: boolean;
    orderIndex: number;
}

const WorkflowStepOrders: FC = () => {
    const { t: translate } = useTranslation(['orders', 'workflow', 'organization']);
    const currentUser = currentUserVar();
    const liveList = useLiveOrderList('stepOrders', getStepOrderListRowTemplate);
    const refreshOrder = useRefreshOrder();
    const [orderId, setOrderId] = useState<string>('');
    const search = useLocation().search;
    const queryParams = new URLSearchParams(search);
    const id = queryParams.get('id');
    const filters = JSON.parse(queryParams.get('filters') || '{}');
    const [manualSort, setManualSort] = useState<{ profileId: string; orderBy: OrderBy } | null>(null);
    const filtersFromStorage = JSON.parse(localStorage.getItem('step-order-filters') || '{}');
    if (filtersFromStorage.status) {
        delete filtersFromStorage.status;
    }
    const [orderFilters, setOrderFilters] = useState<OrderFilter>(Object.assign(filtersFromStorage, filters));
    const rawName = queryParams.get('name');
    const name = !rawName || rawName === 'No Step' || rawName === 'NO_STEP' ? translate('workflow:NO_STEP') : rawName;
    const currentOrganization = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const [batchActionSelected, setBatchActionSelected] = useState(false);
    const [checkedOrders, setCheckedOrders] = useState<Map<string, CheckedOrder>>(new Map());
    const checkedOrderIds = useMemo(() => new Set(checkedOrders.keys()), [checkedOrders]);
    const [batchConfirmDialogOpen, setBatchConfirmDialogOpen] = useState(false);
    const [batchSpinner, setBatchSpinner] = useState(false);
    const [batchError, setBatchError] = useState<Error | null>(null);

    // const { data: departmentsData } = useQuery(departmentsTemplate);
    // const departments: Department[] = departmentsData?.departments || [];
    const departments: Department[] = [];
    const departmentForStep = departments.find((dept) => dept.workflowSteps.some((ws) => ws._id === id));
    const todayStr = new Date().toISOString().slice(0, 10);
    const scheduleUrl = departmentForStep
        ? `/production-dashboard?department=${encodeURIComponent(departmentForStep.name)}&date=${todayStr}`
        : null;

    const isExternalTechnician = permissions.isExternalTechnician(currentUser?.role);
    const isElevatedExternalUser = permissions.isElevatedExternalUser(currentUser?.role);
    const isDentist = permissions.isDentist(currentUser?.type);
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );

    const {
        profiles: listViewProfiles,
        activeProfile: activeListViewProfile,
        activeProfileId: activeListViewProfileId,
        selectProfile: selectListViewProfile,
        loading: profileLoading,
    } = useActiveOrderListProfile({
        enabled: !isExternalTechnician,
        systemDefault: SYSTEM_DEFAULT_WORKFLOW_ORDER_LIST_PROFILE,
        scope: 'WORKFLOW',
    });

    const activeColumnKeys = getActiveOrderListColumnKeys(activeListViewProfile);
    const includeVariables = buildIncludeVariables(activeColumnKeys);
    const includeKey = JSON.stringify(includeVariables);
    const [fetchedIncludeKey, setFetchedIncludeKey] = useState<string>('');

    const profileDefaultOrderBy: OrderBy = useMemo(() => {
        if (!isExternalTechnician && activeListViewProfile.defaultSortField) {
            return {
                orderBy: activeListViewProfile.defaultSortField,
                orderDirection:
                    activeListViewProfile.defaultSortDirection === 'asc' ? OrderDirection.asc : OrderDirection.desc,
            };
        }
        return {
            orderBy:
                filters.status === 'sent_by_customer' && !id ? 'created.on' : 'deliveryDetails.internalDeliveryDate',
            orderDirection: OrderDirection.asc,
        };
    }, [
        activeListViewProfile.defaultSortField,
        activeListViewProfile.defaultSortDirection,
        isExternalTechnician,
        filters.status,
        id,
    ]);

    const orderBy: OrderBy =
        manualSort && manualSort.profileId === activeListViewProfile._id ? manualSort.orderBy : profileDefaultOrderBy;

    const [getStepOrders, stepOrdersResult] = useLazyQuery(getStepOrdersTemplate, {
        context: liveList.context,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        pollInterval: 5 * 60 * 1000,
        notifyOnNetworkStatusChange: true,
        onCompleted: () => {
            setFetchedIncludeKey(includeKey);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { loading, fetchMore, networkStatus, data } = stepOrdersResult;
    const stepOrders = liveList.ordersFrom(data);
    const lastOffset = stepOrders.length;
    const displayOrders = fetchedIncludeKey === includeKey ? stepOrders : [];

    const buildVariables = (overrides: Record<string, unknown> = {}) => ({
        ...(id && { step: id }),
        limit: 20,
        offset: 0,
        filters: resolveTechnicianFilter(orderFilters, currentUser._id),
        ...orderBy,
        ...overrides,
    });

    // Route everyone through getStepOrders. Column profiles are filtered
    // client-side from the full payload. The orderListItems resolver path
    // is retained but not used here due to a memory issue under nav alternation.
    const runQuery = (overrides: Record<string, unknown> = {}) => {
        getStepOrders({ variables: buildVariables(overrides) });
    };

    useEffect(() => {
        if (profileLoading) return;
        runQuery();
    }, [profileLoading, orderFilters, isExternalTechnician, includeKey, orderBy.orderBy, orderBy.orderDirection]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({ variables: buildVariables({ offset: lastOffset }) });
        } else {
            runQuery();
        }
    };

    const handleFilterChange = useCallback((orderFilters: OrderFilter) => {
        setOrderFilters({ ...filters, ...orderFilters });
        setCheckedOrders(new Map());
    }, []);

    const setOrderOnField = (field: string) => {
        const isSameField = orderBy.orderBy === field;
        const direction = isSameField
            ? orderBy.orderDirection === OrderDirection.asc
                ? OrderDirection.desc
                : OrderDirection.asc
            : OrderDirection.desc;
        setManualSort({
            profileId: activeListViewProfile._id,
            orderBy: { orderBy: field, orderDirection: direction },
        });
        setCheckedOrders(new Map());
    };

    const [pausedResumedStep] = useMutation(pausedResumedStepTemplate, {
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<Order[]>) {
            cache.modify({
                fields: {
                    stepOrders(existingStepOrderRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingStepOrderRefs instanceof Array) {
                            return [...existingStepOrderRefs].filter((stepOrderRef: Reference | undefined) => {
                                return orderId !== readField('_id', stepOrderRef);
                            });
                        }
                        return [];
                    },
                },
            });
        },
    });

    const handleUpdateStepList = (orderId: string | undefined) => liveList.removeOrders([orderId]);

    const handlePausedUnpausedStep = async (orderId: string, userId: string, comment: string, reason?: string) => {
        setOrderId(orderId);
        await pausedResumedStep({ variables: { orderId: orderId, userId, comment, reason } });
    };

    const selectedCount = checkedOrders.size;
    const isAtLimit = selectedCount >= BATCH_SELECTION_LIMIT;
    const hasMissingDeliveryNote = Array.from(checkedOrders.values()).some((order) => !order.hasDeliveryNotePdf);

    const handleBatchOrdersPdfPrint = async (type: string) => {
        const orderIds = Array.from(checkedOrders.values())
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((order) => order._id as string);
        setBatchError(null);
        setBatchSpinner(true);
        try {
            await downloadBatchOrdersPdf(orderIds, type, translate);
        } catch (error) {
            setBatchError(error as Error);
        } finally {
            setBatchSpinner(false);
            setBatchActionSelected(false);
            setCheckedOrders(new Map());
        }
    };

    const handleCheckedOrders = useCallback((order: Order, checked: boolean, orderIndex: number) => {
        setCheckedOrders((current) => {
            if (checked && current.size >= BATCH_SELECTION_LIMIT) {
                return current;
            }
            const updated = new Map(current);
            if (checked) {
                updated.set(order._id as string, { ...order, checked, orderIndex });
            } else {
                updated.delete(order._id as string);
            }
            return updated;
        });
    }, []);

    const isAnyOrdersChecked = () => {
        return !!Array.from(checkedOrders.values()).find((value) => value.checked);
    };

    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );
    const [labAvailability, setLabAvailability] = useState<Organization>();

    useQuery(labSettingsTemplate, {
        skip: !isLabMillingEnabled,
        onCompleted({ currentLab }) {
            setLabAvailability({ ...currentLab });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );
    const workflowSteps: Steps[] = currentOrganization.workFlow?.steps || [];

    // Next step comes from each order's own production plan, resolved against the lab's step definitions.
    const { movable: movableOrders, unmovable: unmovableOrders } = useMemo(
        () => splitOrdersByNextStep(Array.from(checkedOrders.values()), workflowSteps, isProductionPlanningEnabled),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [checkedOrders, currentOrganization.workFlow, isProductionPlanningEnabled],
    );

    const [changeWorkflowStepForMany] = useMutation(changeWorkflowStepForManyTemplate);

    // DD-3463: production finished / send to clinic dialogs and their compiled-PDF snackbar.
    // Sent orders leave the workflow list; finished ones stay and show the flag.
    const { openBatchOrderAction, dialogs: batchOrderActionDialogs } = useBatchOrderActions({
        onCompleted: (result, actionType) => {
            const affected = result.succeeded.map((order) => order._id as string);
            if (actionType === BATCH_SEND_TO_CLINIC_ACTION) {
                liveList.removeOrders(affected);
            } else {
                // The batch returns order references, not Orders: re-read the finished rows.
                affected.forEach((orderId) => refreshOrder(orderId));
            }
            setBatchActionSelected(false);
            setCheckedOrders(new Map());
        },
    });

    const handleConfirmMoveToNextStep = async () => {
        setBatchConfirmDialogOpen(false);
        setBatchError(null);
        setBatchSpinner(true);

        // Each order's next step comes from its own production plan, so group by target step.
        const groups = new Map<string, { step: Steps; orderIds: string[] }>();
        movableOrders.forEach(({ order, nextStep }) => {
            const key = nextStep._id as string;
            const group = groups.get(key) || { step: nextStep, orderIds: [] };
            group.orderIds.push(order._id as string);
            groups.set(key, group);
        });

        const movedOrderIds = new Set<string>();
        try {
            for (const { step, orderIds } of Array.from(groups.values())) {
                const millingDeadline =
                    isLabMillingEnabled && step.useMillingDeadline && step.millingDeadlineDays && labAvailability
                        ? millingDeadlineDate(step.millingDeadlineDays as number, labAvailability)
                        : null;
                await changeWorkflowStepForMany({
                    variables: {
                        orderIds,
                        orderCurrentStep: {
                            stepId: step._id,
                            name: step.name,
                            description: step.description,
                            comment: '',
                            maxHourInStep: step.maxHourInStep,
                            userId: currentUser._id,
                            extUsers: step.defaultExtUsers?.map((user) => user._id),
                            millingDeadline,
                            useMillingDeadline: step.useMillingDeadline,
                        },
                    },
                    update(cache: ApolloCache<Order[]>) {
                        cache.modify({
                            fields: {
                                stepOrders(
                                    existingStepOrderRefs: Reference | (Reference | undefined)[],
                                    { readField },
                                ) {
                                    if (existingStepOrderRefs instanceof Array) {
                                        return [...existingStepOrderRefs].filter(
                                            (stepOrderRef: Reference | undefined) => {
                                                return !orderIds.includes(readField('_id', stepOrderRef) as string);
                                            },
                                        );
                                    }
                                    return [];
                                },
                            },
                        });
                    },
                });
                orderIds.forEach((movedOrderId) => movedOrderIds.add(movedOrderId));
            }
            setBatchActionSelected(false);
            setCheckedOrders(new Map());
        } catch (error) {
            setBatchError(error as Error);
            if (movedOrderIds.size) {
                setCheckedOrders((current) => {
                    const updated = new Map(current);
                    movedOrderIds.forEach((movedOrderId) => updated.delete(movedOrderId));
                    return updated;
                });
            }
        } finally {
            setBatchSpinner(false);
            liveList.removeOrders(movedOrderIds);
        }
    };

    return (
        <Fragment>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box mr={2}>
                    <PageTitle headingStyle="h1" title={name} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {scheduleUrl && (
                        <Button component={RouterLink} to={scheduleUrl} startIcon={<CalendarMonthIcon />}>
                            {translate('orders:SHOW_SCHEDULE')}
                        </Button>
                    )}
                    <FormControlLabel
                        checked={batchActionSelected}
                        onChange={() => {
                            setBatchActionSelected(!batchActionSelected);
                            setCheckedOrders(new Map());
                        }}
                        control={<Checkbox />}
                        label={translate('orders:BATCH_ACTION')}
                    />
                    <RecentDeliveryNoteBatches />
                    {!isExternalTechnician ? (
                        <ListViewProfileSelector
                            profiles={listViewProfiles}
                            activeProfileId={activeListViewProfileId}
                            onChange={selectListViewProfile}
                        />
                    ) : undefined}
                </Box>
            </Box>
            <WorkflowOrderFilters
                initFilters={orderFilters}
                onFilterChange={handleFilterChange}
                hideClinicFilter={permissions.isExternalTechnician(currentUser?.role)}
            />
            {loading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.poll ? (
                <DelayedLinearProgress />
            ) : (
                <Fragment>
                    {isExternalTechnician ? (
                        <OrderTableContainer
                            setOrderOnField={setOrderOnField}
                            orderBy={orderBy}
                            hidePatientDetails={permissions.isRestrictedExternalUser(currentUser?.role)}
                            tableType={OrderTableType.EXTERNAL_TECHNICIAN_WORKFLOW}
                            orders={displayOrders}
                            onFetchMore={handleFetchMore}
                            onUpdateStep={handleUpdateStepList}
                            onPausedUnpausedStep={handlePausedUnpausedStep}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={handleCheckedOrders}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    ) : (
                        <OrderTableContainer
                            setOrderOnField={setOrderOnField}
                            orderBy={orderBy}
                            tableType={OrderTableType.LAB_WORKFLOW}
                            orders={displayOrders}
                            onFetchMore={handleFetchMore}
                            onUpdateStep={handleUpdateStepList}
                            onPausedUnpausedStep={handlePausedUnpausedStep}
                            labOrderListProfile={activeListViewProfile}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={handleCheckedOrders}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    )}
                </Fragment>
            )}
            {batchActionSelected && isAnyOrdersChecked() ? (
                <BatchActionBar
                    selectedCount={selectedCount}
                    limit={BATCH_SELECTION_LIMIT}
                    actions={[
                        ...getBatchPdfActions(translate).map((action) => ({
                            ...action,
                            disabled: isBatchPdfActionDisabled(action.type, {
                                isDentist,
                                isExternalTechnician,
                                isElevatedExternalUser,
                                isExternalLabUsersEnabled,
                                hasMissingDeliveryNote,
                            }),
                        })),
                        ...getBatchOrderActions(translate).map((action) => ({
                            ...action,
                            disabled: isBatchOrderActionDisabled(action.type, {
                                isDentist,
                                isExternalTechnician,
                                isElevatedExternalUser,
                            }),
                        })),
                        {
                            icon: <MoveDownIcon />,
                            name: translate('workflow:MOVE_TO_NEXT_STEP'),
                            type: 'MOVE_TO_NEXT_STEP',
                        },
                    ]}
                    onActionClick={(type) => {
                        if (type === 'MOVE_TO_NEXT_STEP') {
                            setBatchConfirmDialogOpen(true);
                            return;
                        }
                        if (openBatchOrderAction(type, Array.from(checkedOrders.keys()))) {
                            return;
                        }
                        if (isBatchPdfActionType(type)) {
                            handleBatchOrdersPdfPrint(type);
                        }
                    }}
                    onClearAll={() => setCheckedOrders(new Map())}
                />
            ) : undefined}
            {batchConfirmDialogOpen ? (
                <BatchMoveNextStepConfirmDialog
                    open={batchConfirmDialogOpen}
                    movableCount={movableOrders.length}
                    unmovableOrders={unmovableOrders}
                    onClose={() => setBatchConfirmDialogOpen(false)}
                    onConfirm={handleConfirmMoveToNextStep}
                />
            ) : undefined}
            {batchSpinner ? (
                <Backdrop open={batchSpinner} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <CircularProgress sx={{ zIndex: 10000, position: 'absolute', top: '50%', left: '50%' }} size={80} />
                </Backdrop>
            ) : undefined}
            {batchError ? <AlertSnackbar type="error" message={batchError?.message} /> : undefined}
            {batchOrderActionDialogs}
        </Fragment>
    );
};

export default WorkflowStepOrders;
